"use client";

import TagIcon from "@/components/TagIcon";
import { isFilled } from "@prismicio/client";
import { PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { useId } from "react";
import "./guide-articles-slider.css";

const titleComponents = {
  heading2: ({ children }) => (
    <h2 className="text-[22px] font-medium leading-[26px] tracking-[0.2px] text-white">
      {children}
    </h2>
  ),
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
};

/**
 * @param {import("@prismicio/client").FilledContentRelationshipField<"article">} article
 */
const GuideArticleCard = ({ article, className = "" }) => (
  <PrismicNextLink field={article} className={`block h-full ${className}`}>
    <article className="flex h-full flex-col gap-4 rounded-2xl bg-blanc px-5 py-5">
      <TagIcon tag={article.data.type} />
      <PrismicRichText
        field={article.data.preview_title}
        components={{
          heading1: ({ children }) => (
            <h3 className="h4-n text-bleu">{children}</h3>
          ),
          heading2: ({ children }) => (
            <h3 className="h4-n text-bleu">{children}</h3>
          ),
        }}
      />
    </article>
  </PrismicNextLink>
);

/**
 * @typedef {import("@prismicio/client").Content.BlogSliceGuideArticlesPrimary} GuideArticlesPrimary
 * @param {{ primary: GuideArticlesPrimary }} props
 */
const GuideArticles = ({ primary }) => {
  const headingId = useId();
  const articles = Array.isArray(primary.articles)
    ? primary.articles
      .map(({ article }) => article)
      .filter(isFilled.contentRelationship)
    : [];

  if (articles.length === 0) {
    return null;
  }

  const carouselAriaProps = isFilled.richText(primary.title)
    ? { "aria-labelledby": headingId }
    : { "aria-label": "Articles du guide" };

  const splideOptions = {
    type: "slide",
    perPage: 2,
    perMove: 1,
    gap: "20px",
    pagination: false,
    arrows: articles.length > 2,
    trimSpace: false,
    breakpoints: {
      1180: {
        perPage: 2,
        gap: "20px",
      },
    },
  };

  return (
    <div className="flex flex-col gap-[30px] overflow-hidden rounded-[20px] bg-bleu py-10 md:px-[30px]">
      {isFilled.richText(primary.title) && (
        <div className="px-[30px] md:px-0">
          <PrismicRichText
            field={primary.title}
            components={{
              ...titleComponents,
              heading2: ({ children }) => (
                <h2
                  id={headingId}
                  className="text-[22px] font-medium leading-[26px] tracking-[0.2px] text-white">
                  {children}
                </h2>
              ),
            }}
          />
        </div>
      )}

      <div className="flex w-full flex-col gap-2.5">
        <div className="flex w-full snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-[30px] pb-2 [-ms-overflow-style:none] [scrollbar-width:none] md:hidden [&::-webkit-scrollbar]:hidden">
          {articles.map((article, index) => (
            <GuideArticleCard
              key={`guide-article-mobile-${index}`}
              article={article}
              className="w-[75vw] lg:w-[84vw] shrink-0 snap-center"
            />
          ))}
        </div>

        <div className="guide-articles-splide hidden w-full md:block">
          <Splide options={splideOptions} {...carouselAriaProps}>
            {articles.map((article, index) => (
              <SplideSlide key={`guide-article-desktop-${index}`} className="h-auto">
                <GuideArticleCard article={article} className="h-full" />
              </SplideSlide>
            ))}
          </Splide>
        </div>
      </div>
    </div>
  );
};

export default GuideArticles;
